import { useEffect, useState } from "react";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chf } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Plus, Download, FileText, Trash2, Copy, AlertTriangle, Tags, ChevronDown, X, Pencil } from "lucide-react";

const WARN_CLS = { danger: "bg-destructive/10 text-destructive border-destructive/30", warn: "bg-amber-50 text-amber-800 border-amber-200", info: "bg-blue-50 text-blue-800 border-blue-200" };
const today = () => new Date().toISOString().slice(0, 10);

function Kpi({ label, value, hint, accent = "stone", testid }) {
  const col = { stone: "text-stone-800", sage: "text-sage", amber: "text-amber-600", champagne: "text-champagne", destructive: "text-destructive" }[accent];
  return (
    <div className="rounded-2xl border bg-white p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-stone-400 leading-tight">{label}</div>
      <div className={`text-lg sm:text-xl font-semibold mt-1 ${col}`}>{value}</div>
      {hint && <div className="text-[11px] text-stone-400 mt-0.5">{hint}</div>}
    </div>
  );
}

export default function Budget() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Divers", estimated_amount: "", vendor_name: "", notes: "" });
  const [expanded, setExpanded] = useState(null);
  const [payItem, setPayItem] = useState(null); // item currently adding a payment to
  const [payForm, setPayForm] = useState({ amount: "", due_date: today(), note: "", method: "" });
  const [editPay, setEditPay] = useState(null); // {id,item, amount,date,note}
  const [newCat, setNewCat] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => { const { data } = await api.get(`/weddings/${wid}/budget`); setD(data); };
  useEffect(() => { if (wid) load(); }, [wid]);

  const addItem = async () => {
    if (!form.title.trim()) { toast.error("Donnez un nom à la dépense"); return; }
    try {
      await api.post(`/weddings/${wid}/budget/items`, {
        title: form.title.trim(), category: form.category, vendor_name: form.vendor_name,
        notes: form.notes, estimated_amount: +form.estimated_amount || 0, final_amount: +form.estimated_amount || 0,
      });
      toast.success("Dépense ajoutée"); setOpen(false);
      setForm({ title: "", category: "Divers", estimated_amount: "", vendor_name: "", notes: "" }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delItem = (it) => setConfirmDel(it);
  const doDelItem = async () => {
    if (!confirmDel) return;
    try { await api.delete(`/weddings/${wid}/budget/items/${confirmDel.id}`); toast.success("Dépense supprimée"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setConfirmDel(null); load(); }
  };
  const dupItem = async (id) => { await api.post(`/weddings/${wid}/budget/items/${id}/duplicate`); toast.success("Dépense dupliquée"); load(); };
  const updateAmount = async (it, amount) => { await api.patch(`/weddings/${wid}/budget/items/${it.id}`, { final_amount: +amount || 0 }); load(); };

  const savePayment = async () => {
    const amt = +payForm.amount;
    if (!amt || amt <= 0) { toast.error("Saisissez un montant"); return; }
    await api.post(`/weddings/${wid}/budget/items/${payItem.id}/payments`, {
      label: payForm.note || "Paiement", amount: amt, due_date: payForm.due_date, note: payForm.note, method: payForm.method, paid: true });
    toast.success("Paiement enregistré"); setPayItem(null);
    setPayForm({ amount: "", due_date: today(), note: "", method: "" }); load();
  };
  const saveEditPay = async () => {
    await api.patch(`/weddings/${wid}/budget/payments/${editPay.id}`, { amount: +editPay.amount || 0, due_date: editPay.due_date, label: editPay.note || "Paiement", note: editPay.note });
    setEditPay(null); load();
  };
  const delPayment = async (id) => { await api.delete(`/weddings/${wid}/budget/payments/${id}`); load(); };
  const addCat = async () => { if (!newCat.trim()) return; await api.post(`/weddings/${wid}/budget/categories`, { name: newCat.trim() }); setNewCat(""); load(); };
  const renameCat = async (c, name) => { await api.patch(`/weddings/${wid}/budget/categories/${c.id}`, { name }); load(); };
  const delCat = async (id) => { await api.delete(`/weddings/${wid}/budget/categories/${id}`); load(); };
  const dlPdf = () => {
    const token = localStorage.getItem("token");
    fetch(`${API}/weddings/${wid}/budget/export/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "budget.pdf"; a.click(); URL.revokeObjectURL(u); });
  };

  if (!d) return <Loader />;
  const cur = d.currency, t = d.totals;

  return (
    <div>
      <PageHeader eyebrow="Suivi financier" title="Budget"
        description="Chaque paiement enregistré est compté immédiatement."
        actions={<>
          <Button variant="outline" onClick={() => setCatOpen(true)} data-testid="manage-categories"><Tags className="w-4 h-4 mr-1" /> Catégories</Button>
          <Button variant="outline" onClick={dlPdf} data-testid="export-budget-pdf"><FileText className="w-4 h-4 mr-1" /> PDF</Button>
          <Button onClick={() => setOpen(true)} data-testid="add-budget-item" className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Ajouter une dépense</Button>
        </>} />

      {/* KPIs — mobile-first, wrap cleanly, no horizontal scroll */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5 mb-5">
        <Kpi testid="kpi-fixed" label="Budget fixé" value={chf(t.fixed, cur)} hint="À ne pas dépasser" accent="stone" />
        <Kpi testid="kpi-planned" label="Dépenses prévues" value={chf(t.planned, cur)} hint="Total des dépenses" accent="stone" />
        <Kpi testid="kpi-paid" label="Déjà payé" value={chf(t.paid, cur)} accent="sage" />
        <Kpi testid="kpi-remaining" label="Reste à payer" value={chf(t.remaining_to_pay, cur)} accent="amber" />
        <Kpi testid="kpi-margin" label="Marge sur budget" value={chf(t.margin, cur)} hint={t.margin < 0 ? "Dépassement" : "Disponible"} accent={t.margin < 0 ? "destructive" : "sage"} />
      </div>

      {(d.warnings || []).length > 0 && (
        <div className="space-y-2 mb-5" data-testid="budget-warnings">
          {d.warnings.map((w, i) => (
            <div key={i} className={`flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl border ${WARN_CLS[w.level] || WARN_CLS.info}`}>
              <AlertTriangle className="w-4 h-4 shrink-0" /> {w.message}</div>
          ))}
        </div>
      )}

      {d.items.length === 0 ? (
        <EmptyState icon={Wallet} title="Votre budget est vide"
          description="Ajoutez vos dépenses, puis enregistrez chaque paiement au fur et à mesure."
          action={<Button onClick={() => setOpen(true)} className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Ajouter une dépense</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {d.items.map((it) => {
            const planned = it.planned || 0;
            const isOpen = expanded === it.id;
            return (
              <div key={it.id} className="rounded-2xl border bg-white overflow-hidden" data-testid={`budget-item-${it.id}`}>
                <div className="p-4">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{it.title}</div>
                      <div className="text-xs text-stone-400 truncate">{it.category}{it.vendor_name && ` · ${it.vendor_name}`}</div>
                    </div>
                    <button onClick={() => dupItem(it.id)} className="p-2 -m-1 rounded-lg text-stone-400 hover:text-stone-700 hover:bg-accent/40" title="Dupliquer"><Copy className="w-4 h-4" /></button>
                    <button onClick={() => delItem(it)} className="p-2 -m-1 rounded-lg text-stone-400 hover:text-destructive hover:bg-destructive/10" data-testid={`del-item-${it.id}`} aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                    <div><div className="text-[10px] uppercase text-stone-400">Prévu</div><div className="font-mono text-sm">{chf(planned, cur)}</div></div>
                    <div><div className="text-[10px] uppercase text-stone-400">Payé</div><div className="font-mono text-sm text-sage">{chf(it.paid_amount, cur)}</div></div>
                    <div><div className="text-[10px] uppercase text-stone-400">Reste</div><div className={`font-mono text-sm ${it.remaining > 0 ? "text-amber-600" : "text-sage"}`}>{chf(it.remaining, cur)}</div></div>
                  </div>
                  {planned > 0 && (
                    <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden mt-3">
                      <div className="h-full bg-sage rounded-full" style={{ width: `${Math.min(100, Math.round((it.paid_amount / planned) * 100))}%` }} /></div>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="flex-1 rounded-full" onClick={() => { setPayItem(it); setPayForm({ amount: "", due_date: today(), note: "", method: "" }); }} data-testid={`add-payment-${it.id}`}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un paiement</Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setExpanded(isOpen ? null : it.id)} data-testid={`details-${it.id}`}>
                      Détails <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${isOpen ? "rotate-180" : ""}`} /></Button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t bg-accent/10 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Label className="text-xs whitespace-nowrap">Montant prévu</Label>
                      <Input type="number" defaultValue={planned} onBlur={(e) => +e.target.value !== planned && updateAmount(it, e.target.value)} className="h-8 w-32" data-testid={`planned-input-${it.id}`} />
                    </div>
                    <div className="text-xs uppercase tracking-wide text-stone-400 mb-2">Historique des paiements ({it.payments.length})</div>
                    {it.payments.length === 0 ? <p className="text-xs text-stone-400">Aucun paiement enregistré.</p> : (
                      <div className="space-y-1.5">
                        {it.payments.map((p) => (
                          <div key={p.id} className="flex items-center gap-2 text-sm bg-white rounded-lg px-3 py-2" data-testid={`payment-${p.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="truncate">{p.label || "Paiement"}</div>
                              <div className="text-[11px] text-stone-400">{p.due_date || ""}{p.method ? ` · ${p.method}` : ""}</div>
                            </div>
                            <span className="font-mono">{chf(p.amount, cur)}</span>
                            <button onClick={() => setEditPay({ id: p.id, amount: p.amount, due_date: p.due_date || today(), note: p.label || "" })} className="text-stone-300 hover:text-stone-700" data-testid={`edit-pay-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => delPayment(p.id)} className="text-stone-300 hover:text-destructive" data-testid={`del-pay-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add payment dialog */}
      <Dialog open={!!payItem} onOpenChange={(o) => !o && setPayItem(null)}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Ajouter un paiement{payItem ? ` — ${payItem.title}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Montant ({cur})</Label><Input type="number" autoFocus value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="mt-1 h-11" data-testid="payment-amount" placeholder="ex. 500" /></div>
            <div><Label>Date</Label><Input type="date" value={payForm.due_date} onChange={(e) => setPayForm({ ...payForm, due_date: e.target.value })} className="mt-1 h-11" data-testid="payment-date" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Note (facultatif)</Label><Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="mt-1 h-11" placeholder="Acompte 1…" /></div>
              <div><Label>Moyen (facultatif)</Label><Input value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className="mt-1 h-11" placeholder="Carte, TWINT…" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={savePayment} className="rounded-full" data-testid="save-payment">Enregistrer le paiement</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit payment dialog */}
      <Dialog open={!!editPay} onOpenChange={(o) => !o && setEditPay(null)}>
        <DialogContent data-testid="edit-payment-dialog">
          <DialogHeader><DialogTitle>Modifier le paiement</DialogTitle></DialogHeader>
          {editPay && (
            <div className="space-y-3">
              <div><Label>Montant ({cur})</Label><Input type="number" value={editPay.amount} onChange={(e) => setEditPay({ ...editPay, amount: e.target.value })} className="mt-1 h-11" data-testid="edit-payment-amount" /></div>
              <div><Label>Date</Label><Input type="date" value={editPay.due_date} onChange={(e) => setEditPay({ ...editPay, due_date: e.target.value })} className="mt-1 h-11" /></div>
              <div><Label>Note</Label><Input value={editPay.note} onChange={(e) => setEditPay({ ...editPay, note: e.target.value })} className="mt-1 h-11" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEditPay} className="rounded-full" data-testid="save-edit-payment">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add item dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Nouvelle dépense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom de la dépense</Label><Input data-testid="budget-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 h-11" placeholder="ex. Photographe cérémonie" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Catégorie</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 h-11 w-full rounded-md border px-2" data-testid="budget-category">
                  {d.categories.map((c) => <option key={c.id}>{c.name}</option>)}</select>
                <p className="text-[11px] text-stone-400 mt-1">Une catégorie sert seulement à classer — plusieurs dépenses peuvent la partager.</p></div>
              <div><Label>Montant prévu ({cur})</Label><Input type="number" data-testid="budget-amount" value={form.estimated_amount} onChange={(e) => setForm({ ...form, estimated_amount: e.target.value })} className="mt-1 h-11" placeholder="ex. 3000" /></div>
            </div>
            <div><Label>Prestataire (facultatif)</Label><Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label className="text-xs">Notes (facultatif)</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="save-budget-item" onClick={addItem} className="rounded-full">Ajouter la dépense</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Categories dialog */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent><DialogHeader><DialogTitle>Catégories de budget</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {d.categories.map((c) => (
              <div key={c.id} className="flex items-center gap-2" data-testid={`cat-${c.id}`}>
                <Input defaultValue={c.name} onBlur={(e) => e.target.value !== c.name && renameCat(c, e.target.value)} className="h-9" />
                <button onClick={() => delCat(c.id)} className="text-stone-300 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2 border-t">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nouvelle catégorie" className="h-10" data-testid="new-cat-input" />
            <Button onClick={addCat} className="rounded-full" data-testid="add-cat"><Plus className="w-4 h-4" /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
